import { q, type Recorder } from './trace';
import type { Area, Vec3 } from '../model/types';

/**
 * Cross-section of a box perpendicular to a flow direction: the area of the box's projection onto a
 * plane normal to the flow. For flow along a box axis this is simply the face area (E.2 Ex. 2 and 5
 * compute Qa / (L × H) and Qa / (B × H)).
 */
export function crossSection(size: Vec3, yawRad: number, flowDirection: Vec3): number {
  const [dxw, dyw, dzw] = flowDirection;
  const len = Math.hypot(dxw, dyw, dzw);
  if (len === 0) return NaN;
  // rotate the world direction into the box frame
  const c = Math.cos(-yawRad);
  const s = Math.sin(-yawRad);
  const dx = (c * dxw - s * dyw) / len;
  const dy = (s * dxw + c * dyw) / len;
  const dz = dzw / len;
  const [lx, ly, lz] = size;
  return Math.abs(dx) * ly * lz + Math.abs(dy) * lx * lz + Math.abs(dz) * lx * ly;
}

export interface AreaVentilation {
  /** Ventilation velocity uw used for dilution, m/s. */
  velocity: number;
  /** Indoor only. */
  airFlow?: number;
  volume?: number;
  crossSection?: number;
  inefficiencyF?: number;
}

export function areaVentilation(area: Area, rec: Recorder): AreaVentilation | undefined {
  const v = area.ventilation;
  if (v.kind === 'outdoor') {
    if (!(v.velocityMPerS > 0)) {
      rec.issue({ code: 'vent.outdoor-velocity', severity: 'error', field: 'area.ventilation.velocityMPerS', message: `Enter a ventilation velocity above zero for ${area.name}.`, ref: { clause: 'C.3.4' } });
      return undefined;
    }
    rec.step({
      id: 'vent.outdoor',
      label: 'Ventilation velocity (outdoor)',
      ref: { clause: 'C.3.4' },
      inputs: {},
      output: { symbol: 'uw', ...q(v.velocityMPerS, 'm/s') },
      notes: [{ kind: 'info', text: v.velocityBasis ?? 'Velocity exceeded 95 % of the time, entered by the user.' }],
    });
    return { velocity: v.velocityMPerS };
  }

  if (!area.box) {
    rec.issue({ code: 'vent.room-size', severity: 'error', field: 'area.box', message: `${area.name} needs room dimensions to work out the ventilation velocity.` });
    return undefined;
  }
  if (!(v.airFlowM3PerS > 0)) {
    rec.issue({ code: 'vent.air-flow', severity: 'error', field: 'area.ventilation.airFlowM3PerS', message: `Enter the fresh air flow for ${area.name}.`, ref: { clause: 'C.3.4' } });
    return undefined;
  }
  const [lx, ly, lz] = area.box.size;
  const volume = lx * ly * lz;
  const a = crossSection(area.box.size, area.box.yawRad, v.flowDirection);
  if (!(a > 0)) {
    rec.issue({ code: 'vent.direction', severity: 'error', field: 'area.ventilation.flowDirection', message: 'Choose the direction the air flows through the room.' });
    return undefined;
  }
  const reduction = v.velocityReductionFactor >= 1 ? v.velocityReductionFactor : 1;
  const velocity = v.airFlowM3PerS / a / reduction;
  rec.step({
    id: 'vent.indoor',
    label: 'Ventilation velocity (indoor average)',
    ref: { clause: 'C.3.4' },
    inputs: { Qa: q(v.airFlowM3PerS, 'm3/s'), A: q(a, 'm2'), reduction: q(reduction, '1') },
    output: { symbol: 'uw', ...q(velocity, 'm/s') },
    notes: [{ kind: 'info', text: 'Air flow divided by the room cross-section perpendicular to the flow, reduced for inefficient or obstructed flow.' }],
  });
  return { velocity, airFlow: v.airFlowM3PerS, volume, crossSection: a, inefficiencyF: v.inefficiencyF };
}
